import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import {
  AiAgentChat,
  BottomNav,
  OnboardingTour,
  RouteSkeleton,
  SensitiveDataConsentGate,
  TermsGate,
} from './components';
import { queryClient } from './api/queryClient';
import { ThemeProvider } from './context/ThemeContext';
import { AiAgentProvider } from './context/AiAgentContext';
import { ToastProvider } from './context/ToastContext';
import useOnboarding from './use/useOnboarding';
import useTerms from './use/useTerms';
import useSensitiveDataConsent from './use/useSensitiveDataConsent';
import useCurrentUser from './use/useCurrentUser';

// Uma página por chunk. Antes as 26 entravam no bundle inicial — inclusive as 6
// de administração, que só o DOMINUS abre. Quem segura a tela enquanto o chunk
// baixa é o Suspense de cada layout, logo abaixo.
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Quiz = lazy(() => import('./pages/Quiz'));
const QuizFeedback = lazy(() => import('./pages/QuizFeedback'));
const RoutineChecklist = lazy(() => import('./pages/RoutineChecklist'));
const Progress = lazy(() => import('./pages/Progress'));
const ProgressHistory = lazy(() => import('./pages/ProgressHistory'));
const SkinProgress = lazy(() => import('./pages/SkinProgress'));
const RoutineProgress = lazy(() => import('./pages/RoutineProgress'));
const ProductLibrary = lazy(() => import('./pages/ProductLibrary'));
const SkinPhotoComparison = lazy(() => import('./pages/SkinPhotoComparison'));
const Settings = lazy(() => import('./pages/Settings'));
const Subscription = lazy(() => import('./pages/Subscription'));
const EmailVerification = lazy(() => import('./pages/EmailVerification'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AdminPortal = lazy(() => import('./pages/AdminPortal'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminBugs = lazy(() => import('./pages/AdminBugs'));
const AdminBilling = lazy(() => import('./pages/AdminBilling'));
const AdminProducts = lazy(() => import('./pages/AdminProducts'));
const AdminNotifications = lazy(() => import('./pages/AdminNotifications'));

/**
 * Fronteira de carregamento + entrada de uma troca de tela.
 *
 * O `key={pathname}` é o que faz a animação REPETIR a cada navegação: sem ele o
 * nó é o mesmo e a entrada roda uma vez só, na montagem.
 *
 * A entrada é só opacidade, e curta: a BottomNav é tocada dezenas de vezes por
 * sessão, e aí motion vira espera. Sem animação de saída pelo mesmo motivo — ela
 * só atrasaria a navegação.
 */
function Rota({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();

  return (
    <div key={pathname} className="animate-fade-in">
      <Suspense fallback={<RouteSkeleton />}>{children}</Suspense>
    </div>
  );
}

interface LayoutProps {
  children: React.ReactNode;
}

function PrivateLayout({ children }: LayoutProps) {
  const token = localStorage.getItem('authToken');
  const navigate = useNavigate();
  const { showTour, completeTour } = useOnboarding();
  const { precisaAceitar, aceitar, salvando, erro } = useTerms();
  const consentimento = useSensitiveDataConsent();
  const { pathname } = useLocation();

  if (!token) return <Navigate to="/login" replace />;

  // A autorização dos dados de saúde vem depois dos Termos e fica fora de Configurações e de
  // Planos: cancelar a assinatura, baixar os dados ou excluir a conta não podem depender de
  // autorizar. "Agora não" leva justamente para Configurações.
  const consentimentoAqui =
    consentimento.precisaAutorizar && !precisaAceitar && !['/settings', '/subscription'].includes(pathname);

  // O tour navega sozinho para /dashboard (passo 1). Na tela de planos isso vira
  // loop infinito: conta nova sem assinatura toma 402 no dashboard, o axiosConfig
  // devolve para /subscription, o tour monta de novo e empurra para o dashboard.
  // Quem está no paywall não tem o que passear — o tour espera o pagamento.
  // Os dois portões (Termos e dados de saúde) vêm antes de tudo: overlays juntos não dá.
  const tourAqui = showTour && !precisaAceitar && !consentimentoAqui && pathname !== '/subscription';

  return (
    <AiAgentProvider>
      {/* A fronteira fica aqui dentro, e não em volta das Routes, para a BottomNav
          e o chat não piscarem na troca de aba: só o miolo troca. */}
      <Rota>{children}</Rota>
      <BottomNav />
      <AiAgentChat />
      <TermsGate aberto={precisaAceitar} onAccept={aceitar} loading={salvando} error={erro} />
      <SensitiveDataConsentGate
        aberto={consentimentoAqui}
        onAutorizar={consentimento.autorizar}
        onAgoraNao={() => navigate('/settings')}
        loading={consentimento.salvando}
        error={consentimento.erro}
      />
      <OnboardingTour aberto={tourAqui} onComplete={completeTour} onSkip={completeTour} />
    </AiAgentProvider>
  );
}

function AdminLayout({ children }: LayoutProps) {
  const token = localStorage.getItem('authToken');
  const { isAdmin } = useCurrentUser();

  if (!token) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return <Rota>{children}</Rota>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <Router>
            {/* Fronteira das rotas públicas; as privadas e as de admin têm a delas. */}
            <Rota>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify-email" element={<EmailVerification />} />
                {/* Públicas: Termos e Política precisam ser legíveis antes de existir conta. */}
                <Route path="/termos" element={<Terms />} />
                <Route path="/privacidade" element={<Privacy />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/dashboard" element={<PrivateLayout><Dashboard /></PrivateLayout>} />
                <Route path="/quiz" element={<PrivateLayout><Quiz /></PrivateLayout>} />
                {/* Anamnese de feedback dos 30 dias: adapta a rotina em vez de refazer o quiz. */}
                <Route path="/anamnesis-feedback" element={<PrivateLayout><QuizFeedback /></PrivateLayout>} />
                {/* A antiga tela de gerir rotina virou o próprio dashboard, que agora edita no lugar. */}
                <Route path="/routine" element={<Navigate to="/dashboard" replace />} />
                <Route path="/routine-checklist" element={<PrivateLayout><RoutineChecklist /></PrivateLayout>} />
                <Route path="/progress" element={<PrivateLayout><Progress /></PrivateLayout>} />
                <Route path="/progress-history" element={<PrivateLayout><ProgressHistory /></PrivateLayout>} />
                <Route path="/skin-progress" element={<PrivateLayout><SkinProgress /></PrivateLayout>} />
                <Route path="/routine-progress" element={<PrivateLayout><RoutineProgress /></PrivateLayout>} />
                <Route path="/products" element={<PrivateLayout><ProductLibrary /></PrivateLayout>} />
                <Route path="/skin-photos" element={<PrivateLayout><SkinPhotoComparison /></PrivateLayout>} />
                <Route path="/settings" element={<PrivateLayout><Settings /></PrivateLayout>} />
                <Route path="/subscription" element={<PrivateLayout><Subscription /></PrivateLayout>} />
                <Route path="/admin-portal" element={<AdminLayout><AdminPortal /></AdminLayout>} />
                <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
                <Route path="/admin/bugs" element={<AdminLayout><AdminBugs /></AdminLayout>} />
                <Route path="/admin/billing" element={<AdminLayout><AdminBilling /></AdminLayout>} />
                <Route path="/admin/products" element={<AdminLayout><AdminProducts /></AdminLayout>} />
                <Route path="/admin/notifications" element={<AdminLayout><AdminNotifications /></AdminLayout>} />
                <Route path="/" element={<Navigate to="/login" replace />} />
              </Routes>
            </Rota>
          </Router>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
